import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { getUser } from "../../utils/auth";
import resolveImageUrl from "../../utils/imageUtils";

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import {
  hasInspectorFinalDecision,
  isSupervisorDecisionLocked,
} from "../../utils/flaggedSupervisorUtils";
import {
  MapPin,
  Home,
  Layers,
  Building,
  XCircle,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X,
  Save,
  Edit,
  AlertCircle,
  RotateCw,
  Info,
  TrendingUp,
  Maximize2,
  CheckCircle,
  Calendar,
} from "lucide-react";

const ITEMS_PER_PAGE = 8;

const Assigned = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [comment, setComment] = useState("");
  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [showNoReassessDetailsModal, setShowNoReassessDetailsModal] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [notReassessComment, setNotReassessComment] = useState("");
  const [features, setFeatures] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all | pending | responded
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [highlightPin, setHighlightPin] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [pt6IssueDate, setPt6IssueDate] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const user = getUser();
  const userId = user?.id;
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined";
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const pickFirstValue = (property, keys) => {
    for (const key of keys) {
      const value = property?.[key];
      if (hasUsableValue(value)) return value;
    }
    return "";
  };

  const getMLDisplayValue = (property, field) => {
    let realValue = "";
    switch (field) {
      case "floor":
        realValue = pickFirstValue(property, [
          "floors_ml",
          "floor_ml",
          "floors_pred",
          "floor_pred",
        ]);
        break;
      case "landuse":
        realValue = pickFirstValue(property, [
          "landuse_ml",
          "land_use_ml",
          "land_use_pred",
          "landuse_pred",
        ]);
        break;
      case "landarea":
        {
          const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
          const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
      case "coveredarea":
        {
          const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
          const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
    }

    if (hasUsableValue(realValue)) {
      if (field === "landarea" || field === "coveredarea") {
        // Check if it's a range string first
        const strVal = String(realValue);
        const isRange = strVal.toLowerCase().includes("greater") ||
          strVal.toLowerCase().includes("less") ||
          strVal.toLowerCase().includes("between");
        if (isRange) {
          return realValue;
        }
        return formatTwoDecimals(realValue) || "N/A";
      }
      const num = Number(realValue);
      if (Number.isFinite(num) && !isNaN(num)) {
        return Number.isInteger(num) ? num : Math.round(num);
      }
      return realValue;
    }
    return "N/A";
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalize = (val) => {
      if (val === null || val === undefined) return null;
      let raw = String(val).trim();
      if (!raw) return null;
      // Strip out units like "sq yards", "sq yds", "sq ft", "sq feet", "sq.yds", "sq.ft", etc. just like parseRange does
      raw = raw.replace(/sq\.?\s*(yards?|yds?|ft|feet?)/gi, '').replace(',', '').trim();
      const lowered = raw.toLowerCase();
      if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    // For landarea and coveredarea, check if admin is within ML range
    if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
      const range = parseRange(mlVal);
      const a = normalize(adminVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return a <= range.value; // highlight if admin is NOT greater
          case "less":
            return a >= range.value; // highlight if admin is NOT less
          case "between":
            return a < range.min || a > range.max; // highlight if strictly outside range (inclusive of min/max)
          case "exact":
            return Math.abs(a - range.value) > 1e-9; // highlight if different
        }
      }
    }

    // For all other fields, use original comparison
    const a = normalize(adminVal);
    const m = normalize(mlVal);
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const fetchProperties = async () => {
    try {
      const user = getUser();
      if (!user || !user.id) {
        setError("Inspector ID not found");
        return [];
      }

      // const res = await axios.get(`/inspector/assigned/${user.id}`, { params: { includeAll: true } });
      // const rows = dedupeByPin(res.data || []);
      // setProperties(rows);
      // return rows;
      const res = await axios.get(`/inspector/assigned/${user.id}`, { params: { includeAll: true } });
      const rows = dedupeByPin(res.data || []);
      // Stamp every fresh fetch with its own bust value so a page refresh
      // always forces a real network request for the current image bytes,
      // instead of trusting whatever the browser/CDN has cached at the plain URL.
      const fetchBust = Date.now();
      const bustedRows = rows.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust }));
      setProperties(bustedRows);
      return bustedRows;
    } catch (err) {
      console.error(err);
      setError("Failed to load assigned properties");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedProperty = async (propertyId) => {
    const rows = await fetchProperties();
    if (!propertyId) return null;
    const fresh = rows.find((p) => p.id === propertyId) || null;
    if (fresh) {
      setSelectedProperty(fresh);
      await fetchTimeline(fresh.pin, fresh.id);
    }
    return fresh;
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchTimeline = async (pin, propertyId) => {
    if (!pin) {
      setTimeline([]);
      setTimelineError("");
      setTimelineLoading(false);
      return;
    }
    try {
      setTimelineLoading(true);
      setTimelineError("");
      const params = propertyId ? { propertyId } : {};
      const res = await axios.get(`/properties/timeline-id/${selectedProperty.id}`, { params });
      setTimeline(res.data?.timeline || []);
    } catch (err) {
      console.error("Failed to fetch timeline:", err);
      setTimeline([]);
      setTimelineError("Failed to load previous comments.");
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline(selectedProperty?.pin, selectedProperty?.id);
  }, [selectedProperty?.pin, selectedProperty?.id]);

  // const hasResponded = (property) => hasInspectorFinalDecision(property, userId);

  // Only the authoritative reassess field should count as an inspector decision here.
  // Don't pass the supervisor's own userId — property.updated_by gets set to the
  // supervisor's ID when THEY flag it, which falsely matched as "inspector decided."
  const hasResponded = (property) => hasInspectorFinalDecision(property);

  const pendingCount = useMemo(
    () => (properties || []).filter((p) => !hasResponded(p)).length || 0,
    [properties]
  );

  const respondedCount = useMemo(
    () => (properties || []).filter((p) => hasResponded(p)).length || 0,
    [properties]
  );

  const filteredProperties = useMemo(() => {
    if (filterStatus === "pending") return properties.filter((p) => !hasResponded(p));
    if (filterStatus === "responded") return properties.filter((p) => hasResponded(p));
    return properties;
  }, [properties, filterStatus]);

  function highlightPropertyCard(pin) {
    const normalized = normalizePin(pin);
    if (!normalized) return;

    const idx = filteredProperties.findIndex(
      (p) => normalizePin(p.pin) === normalized
    );
    if (idx >= 0) {
      const targetPage = Math.floor(idx / ITEMS_PER_PAGE) + 1;
      setPage(targetPage);
    }

    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setSelectedProperty(null);
    setRotation(0);
    highlightPropertyCard(pinToHighlight);
    navigate({ search: "" }, { replace: true });
  };

  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;
    if (!properties.length) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );

    if (matched) {
      setSelectedProperty(matched);
      setAutoOpenedPin(targetPinFromQuery);
      return;
    }

    axios
      .get(`/inspector/property/${encodeURIComponent(targetPinFromQuery)}`)
      .then((res) => {
        if (res.data?.pin) {
          setSelectedProperty(res.data);
          setAutoOpenedPin(targetPinFromQuery);
        }
      })
      .catch(() => { });
  }, [targetPinFromQuery, autoOpenedPin, properties]);

  // Auto submit reassessment when readyToSubmit is true
  useEffect(() => {
    if (readyToSubmit && selectedProperty) handleSubmitReassess();
  }, [readyToSubmit]);

  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE);
  const paginated = filteredProperties.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    // Our features don't have pt key, so we don't need to update features here
  };

  const setFeaturesForReassess = (p) => {
    // When editing a previous reassessment, show the saved inspector values
    // (same priority as the detail "Reassessment" column: *_ml then *_pred).
    const wasPreviouslyReassessed = p.reassess == 1;

    const getPreviousInspectorValue = (mlValue, predKeys) => {
      if (!wasPreviouslyReassessed) return "";
      if (hasUsableValue(mlValue)) return mlValue;
      return pickFirstValue(p, predKeys) || "";
    };

    setFeatures([
      {
        key: "floor",
        label: "Floor",
        admin: p.floor_admin || "",
        ml: p.floors_ml || p.floors_pred || "",
        inspector: getPreviousInspectorValue(p.floors_ml, ["floors_pred", "floor_pred"]),
        unit: "",
      },
      {
        key: "landuse",
        label: "Land Use",
        admin: p.landuse_admin || "",
        ml: p.landuse_ml || p.land_use_pred || "",
        inspector: getPreviousInspectorValue(p.landuse_ml, ["land_use_pred", "landuse_pred"]),
        unit: "",
      },
      {
        key: "landarea",
        label: "Land Area",
        admin: p.landarea_admin || "",
        ml: p.landarea_ml || p.landarea_combined_pred || "",
        inspector: getPreviousInspectorValue(p.landarea_ml, ["landarea_combined_pred"]),
        unit: "sq.yards",
      },
      {
        key: "coveredarea",
        label: "Covered Area",
        admin: p.coveredarea_admin || "",
        ml: p.coveredarea_ml || p.coveredarea_combined_pred || "",
        inspector: getPreviousInspectorValue(p.coveredarea_ml, ["coveredarea_combined_pred"]),
        unit: "sq.ft",
      },
    ]);
    setRowExplanations({
      floor: wasPreviouslyReassessed ? (p.inspector_floor_explanation || "") : "",
      landuse: wasPreviouslyReassessed ? (p.inspector_landuse_explanation || "") : "",
      landarea: wasPreviouslyReassessed ? (p.inspector_landarea_explanation || "") : "",
      coveredarea: wasPreviouslyReassessed ? (p.inspector_coveredarea_explanation || "") : "",
    });
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];

      let validatedValue = value;
      if (field === "inspector") {
        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }

      updated[index][field] = validatedValue;
      return updated;
    });
  };

  const updateRowExplanation = (key, value) => {
    setRowExplanations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveComment = async (commentText, propertyId) => {
    if (!commentText.trim()) return null;

    try {
      await axios.post("/properties/add-comment", {
        user_id: userId,
        property_id: propertyId,
        comment: commentText.trim(),
      });
      return true;
    } catch (err) {
      console.error("Failed to save comment:", err);
      return false;
    }
  };

  const handleSaveCommentOnly = async () => {
    if (!comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      const saved = await saveComment(comment, selectedProperty.id);
      if (saved) {
        toast.success("Comment saved successfully");
        setComment("");
        await fetchTimeline(selectedProperty.pin, selectedProperty.id);
      } else {
        toast.error("Failed to save comment");
      }
    } catch {
      toast.error("Failed to save comment");
    }
  };

  const handleNoReassess = async () => {
    if (!notReassessComment.trim()) return;
    try {
      setSubmitting(true);
      setActionType("no-reassess");

      const finalComment = [
        comment.trim(),
        `Reason for not reassessing: ${notReassessComment.trim()}`,
      ].filter(Boolean).join("\n\n");

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("status", "unresolved");

      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      if (selectedProperty.floors_ml) fd.append("floors_ml", selectedProperty.floors_ml);
      if (selectedProperty.landuse_ml) fd.append("landuse_ml", selectedProperty.landuse_ml);
      if (selectedProperty.landarea_ml) fd.append("landarea_ml", selectedProperty.landarea_ml);
      if (selectedProperty.coveredarea_ml) fd.append("coveredarea_ml", selectedProperty.coveredarea_ml);

      const existingRefno =
        referenceNumber ||
        selectedProperty.refno ||
        selectedProperty.pt5 ||
        selectedProperty.pt_5 ||
        selectedProperty.pt ||
        "";
      if (existingRefno) {
        fd.append("refno", existingRefno);
      }

      fd.append("comment", finalComment);
      fd.append("inspector_comment", finalComment);

      if (userId && selectedProperty?.id) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: finalComment,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      const commentSaved = await saveComment(finalComment, selectedProperty.id);

      if (!commentSaved) {
        toast.error("Failed to save comment, but proceeding with update...");
      }

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      await trackAction(
        "flagged_by_Supervisor_edit_do_not_reassess",
        selectedProperty.pin,
        buildClickDetails({
          inspectorText: finalComment,
        })
      );

      toast.success("Successfully marked as 'Do Not Reassess'");

      setShowReassessModal(false);
      setShowNoReassessDetailsModal(false);
      setShowNotReassessModal(false);
      setNotReassessComment("");
      setComment("");
      setActionType(null);
      await refreshSelectedProperty(selectedProperty.id);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride, pt6IssueDateOverride } = {}) => {
    const activeFeatures = featuresOverride || features;
    const activeRefno = refnoOverride || referenceNumber;
    const activePt6Date = pt6IssueDateOverride ?? pt6IssueDate;

    if (!comment.trim()) {
      toast.error("Overall Explanation is mandatory.");
      return;
    }

    // if (!String(activeRefno || "").trim()) {
    //   toast.error("PT-5 Serial Number is mandatory for reassessment.");
    //   return;
    // }

    try {
      setSubmitting(true);
      setActionType("reassess");

      let finalComment = comment.trim();
      if (!finalComment) {
        finalComment = "Property reassessed with updated features";
      } else {
        finalComment = `${finalComment} (Property reassessed)`;
      }

      // First save explanation
      await axios.post("/inspector/save-explanation", {
        property_id: selectedProperty.id,
        user_id: userId,
        overall_explanation: finalComment,
        row_explanations: rowExplanations
      });

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");
      fd.append("status", "resolved");
      fd.append("status", "unresolved");

      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      // Preserve existing values, then overlay inspector edits onto both
      // *_ml (Flagged-by-Supervisor display) and *_pred (canonical reassessment fields).
      const featureUpdates = {
        floors_ml: selectedProperty.floors_ml || "",
        landuse_ml: selectedProperty.landuse_ml || "",
        landarea_ml: selectedProperty.landarea_ml || "",
        coveredarea_ml: selectedProperty.coveredarea_ml || "",
        floors_pred: selectedProperty.floors_pred || "",
        land_use_pred: selectedProperty.land_use_pred || "",
        landarea_combined_pred: selectedProperty.landarea_combined_pred || "",
        coveredarea_combined_pred: selectedProperty.coveredarea_combined_pred || "",
      };

      activeFeatures.forEach((f) => {
        const val = f.inspector?.toString().trim();
        if (!val) return;

        switch (f.key) {
          case "floor":
            featureUpdates.floors_ml = val;
            featureUpdates.floors_pred = val;
            break;
          case "landuse":
            featureUpdates.landuse_ml = val;
            featureUpdates.land_use_pred = val;
            break;
          case "landarea":
            featureUpdates.landarea_ml = val;
            featureUpdates.landarea_combined_pred = val;
            break;
          case "coveredarea":
            featureUpdates.coveredarea_ml = val;
            featureUpdates.coveredarea_combined_pred = val;
            break;
        }
      });

      Object.entries(featureUpdates).forEach(([key, value]) => {
        fd.append(key, value);
      });

      if (selectedProperty.exp_garv) fd.append("exp_garv", selectedProperty.exp_garv);

      // Add PT-5 Serial number
      if (activeRefno) {
        fd.append("refno", activeRefno);
      }

      // Add PT-6 Issue Date
      if (activePt6Date) {
        fd.append("pt6_issue_date", activePt6Date);
      }

      fd.append("inspector_comment", finalComment);

      const commentSaved = await saveComment(finalComment, selectedProperty.id);

      if (!commentSaved) {
        toast.error("Failed to save comment, but proceeding with reassessment...");
      }

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      const featureChanges = collectFeatureChanges(activeFeatures, null, {
        valueField: "inspector",
        getInitialVal: (feature) => feature.ml?.toString().trim() || feature.admin?.toString().trim(),
      });

      await trackAction(
        "flagged_by_S_edit_reassess_submit_reassessment",
        selectedProperty.pin,
        buildClickDetails({
          featureChanges: featureChanges.length ? featureChanges : ["No feature changes"],
          inspectorText: collectInspectorText({ freeTextComment: finalComment }),
        })
      );

      toast.success("Reassessment saved successfully!");

      setShowReassessModal(false);
      setShowRefModal(false);
      setComment("");
      setFeatures([]);
      setActionType(null);
      setReadyToSubmit(false);
      await refreshSelectedProperty(selectedProperty.id);
    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: features,
          refnoOverride: newPt5,
          pt6IssueDateOverride: pt6IssueDate,
        });
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassessAction = (type) => {
    setActionType(type);

    if (type === "no-reassess") {
      setComment("");
      setReferenceNumber(
        selectedProperty.refno ||
        selectedProperty.pt5 ||
        selectedProperty.pt_5 ||
        selectedProperty.pt ||
        ""
      );
      setNotReassessComment("");
      setShowNoReassessDetailsModal(true);
    } else if (type === "reassess") {
      setFeaturesForReassess(selectedProperty);
      setReferenceNumber(selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "");
      setPt6IssueDate("");
      setShowReassessModal(true);
    }
  };

  const hasPredictions = (property) => {
    return (
      property.floors_pred ||
      property.land_use_pred ||
      property.landarea_combined_pred ||
      property.coveredarea_combined_pred
    );
  };

  const hasMLPredictions = (property) => {
    return hasUsableValue(
      pickFirstValue(property, [
        "floors_ml",
        "floors_pred",
        "floor_ml",
        "floor_pred",
        "floor",
        "landuse_ml",
        "land_use_pred",
        "landuse_pred",
        "land_use",
        "landarea_ml",
        "landarea_combined_pred",
        "land_area_ml",
        "land_area",
        "coveredarea_ml",
        "coveredarea_combined_pred",
        "covered_area_ml",
        "covered_area",
      ])
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex-1 p-6 md:p-8">
        <Toaster position="top-right" />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Assigned Properties</h1>
              <p className="text-gray-600 mt-2">Review and inspect properties assigned to you</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {/*  <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                Total: <span className="font-semibold text-blue-600">{properties.length}</span> properties
              </div> */}
              <div className="text-xs sm:text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
                Showing <span className="font-semibold">{paginated.length}</span> of{" "}
                <span className="font-semibold">{filteredProperties.length}</span> properties
              </div>
            </div>
          </div>
        </div>

        {!loading && !error && properties.length > 0 && (
          <div className="mb-8">
            <div className="inline-flex bg-white rounded-xl p-2 border border-gray-200 shadow-sm gap-2">
              <button
                type="button"
                onClick={() => setFilterStatus("all")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${filterStatus === "all"
                  ? "bg-purple-600 text-white shadow"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                All ({(properties || []).length || 0})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus("pending")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${filterStatus === "pending"
                  ? "bg-purple-600 text-white shadow"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Pending ({pendingCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus("responded")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${filterStatus === "responded"
                  ? "bg-purple-600 text-white shadow"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Responded ({respondedCount})
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading assigned properties...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center shadow-sm">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Properties</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchProperties}
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors shadow-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && properties.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Assigned</h3>
            <p className="text-gray-500">You don't have any properties assigned for inspection yet.</p>
          </div>
        )}

        {!loading && !error && properties.length > 0 && filteredProperties.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Found</h3>
            <p className="text-gray-500">
              {filterStatus === "pending"
                ? "No pending properties."
                : filterStatus === "responded"
                  ? "No responded properties."
                  : "No properties found."}
            </p>
          </div>
        )}

        {/* Property Grid */}
        {!loading && paginated.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginated.map((property) => (
                <motion.div
                  key={property.id}
                  data-pin={normalizePin(property.pin)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white shadow hover:shadow-lg cursor-pointer transition-all relative rounded-xl ${normalizePin(property.pin) && normalizePin(property.pin) === highlightPin
                    ? "border-2 border-sky-500"
                    : "border border-gray-200"
                    }`}
                  onClick={() => setSelectedProperty(property)}
                >
                  {normalizePin(property.pin) && normalizePin(property.pin) === highlightPin && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  <div className="h-48 bg-gray-100 overflow-hidden relative group rounded-t-xl">
                    {property.image ? (
                      <>
                        <img
                          src={appendCacheBuster(resolveImageUrl(property.image), property._imgBust)}
                          alt="Property"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="bg-white/90 p-3 rounded-full">
                            <Maximize2 className="w-6 h-6 text-gray-800" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-yellow-50 to-blue-50">
                        <span className="text-4xl">🏠</span>
                      </div>
                    )}
                    {/* {property.status === "flagged" && (
                      <div className="absolute top-3 left-3 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">
                        🚩 Flagged by Supervisor
                      </div>
                    )} */}
                    {/* <div className="absolute top-3 right-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          hasResponded(property) ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"
                        }`}
                      >
                        {hasResponded(property) ? "✓ Responded" : "⏳ Pending"}
                      </span>
                    </div> */}
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">PIN: {property.pin}</h3>
                    {property.meeting_requested == 1 && !property.meeting_resolved && (
                      <div className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-1">
                        <Calendar className="h-3 w-3" /> Meeting Requested
                      </div>
                    )}

                    <div className="space-y-2 text-sm text-gray-600">
                      <div>Circle: {property.circle || "N/A"}</div>

                      {/* {(property.supervisor_flag_comment || property.supervisor_reason) && (
                        <div className="text-xs text-gray-500 line-clamp-2">
                          Supervisor comment: {(property.supervisor_flag_comment || property.supervisor_reason).toString()}
                        </div>
                      )} */}

                      {/* {(property.inspector_overall_explanation || property.inspector_last_comment) && (
                        <div className="text-xs text-slate-600 line-clamp-2">
                          Your comment:{" "}
                          {(property.inspector_overall_explanation || property.inspector_last_comment).toString()}
                        </div>
                      )} */}

                      {/* <div className="grid grid-cols-1 gap-1 mt-3 py-2 border-y border-gray-100 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Floor (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.floor_admin) ? property.floor_admin : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Use (Admin):</span>
                          <span className="font-medium truncate ml-2 text-right max-w-[160px]">
                            {hasUsableValue(property.landuse_admin) ? property.landuse_admin : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Area (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.landarea_admin)
                              ? `${formatTwoDecimals(property.landarea_admin) || property.landarea_admin} sq.yards`
                              : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Covered Area (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.coveredarea_admin)
                              ? `${formatTwoDecimals(property.coveredarea_admin) || property.coveredarea_admin} sq.ft`
                              : "N/A"}
                          </span>
                        </div>
                      </div> */}

                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                      <button className="flex-1 py-2 text-sm font-medium text-yellow-700 hover:text-yellow-900 transition-colors">
                        View Details →
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-3 mt-8 pt-6 border-t border-gray-200 pb-20">
                <div className="text-sm text-gray-600">
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(page * ITEMS_PER_PAGE, filteredProperties.length)} of {filteredProperties.length} properties
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-10 h-10 flex items-center justify-center text-sm rounded-lg transition-all shadow-sm hover:shadow ${page === p
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                          : "text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Property Details Modal - Full screen fit with no scroll */}
        {selectedProperty && !showReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
            <div className="bg-white w-full h-full flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-xl font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                    {/*  <div className="text-right">
                      <p className="text-gray-500 text-sm">Updated By</p>
                      <p className="text-sm font-semibold text-gray-700">{selectedProperty.updated_by || "N/A"}</p>
                    </div> */}
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <p className="text-base">PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span></p>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {selectedProperty.status === "flagged" && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800">
                        🚩 Flagged by Supervisor
                      </span>
                    )}
                    {/* {hasInspectorFinalDecision(selectedProperty, userId) && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${selectedProperty.reassess == 1
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                          }`}
                      >
                        {selectedProperty.reassess == 1 ? "✓ Reassessed" : "✗ Do Not Reassess"}
                      </span>
                    )} */}
                    {selectedProperty.meeting_requested == 1 && !selectedProperty.meeting_resolved && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-800">
                        <Calendar className="h-3.5 w-3.5" /> Meeting Requested
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="ml-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  data-click-id="flagged_by_Supervisor_close"
                  data-property-id={selectedProperty?.pin}
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div
                    className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden h-[420px] flex items-center justify-center shadow-inner cursor-pointer border border-gray-200"
                    onClick={() => selectedProperty.image && setShowFullImage(true)}
                  >
                    {selectedProperty.image ? (
                      <>
                        <div
                          className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                          style={{ transform: `rotate(${rotation}deg)` }}
                        >
                          <img
                            src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                            alt="Property"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex flex-col items-center justify-center gap-2">
                          <Maximize2 className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-8 w-8" />
                          <span className="text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Click to Enlarge
                          </span>
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRotate(e);
                            }}
                            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <RotateCw className="h-5 w-5" />
                          </button>
                          {rotation !== 0 && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <CheckCircle className="h-5 w-5" />
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-6">
                        <Home className="h-20 w-20 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium text-lg">No Image Available</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border rounded-lg overflow-hidden text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-2 text-left font-medium">Feature</th>
                            <th className="p-2 text-left font-medium">Admin</th>
                            {/* <th className="p-2 text-left font-medium">Computer Vision</th> */}
                            {selectedProperty.reassess == 1 && (
                              <th className="p-2 text-left font-medium">Reassessment</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {/* <tr className={`border-b ${areValuesDifferent(selectedProperty.floor_admin, getMLDisplayValue(selectedProperty, "floor"), "floor") ? "bg-red-100" : ""}`}> */}
                          <tr className="border-b">
                            <td className="p-2 font-medium">Floor</td>
                            <td className="p-2">{hasUsableValue(selectedProperty.floor_admin) ? selectedProperty.floor_admin : "N/A"}</td>
                            {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "floor")}</td> */}
                            {selectedProperty.reassess == 1 && (
                              <td className="p-2">
                                {(() => {
                                  const reassessVal = hasUsableValue(selectedProperty.floors_ml)
                                    ? selectedProperty.floors_ml
                                    : selectedProperty.floors_pred;
                                  return hasUsableValue(reassessVal) ? reassessVal : "N/A";
                                })()}
                              </td>
                            )}
                          </tr>
                          {/* <tr className={`border-b ${areValuesDifferent(selectedProperty.landuse_admin, getMLDisplayValue(selectedProperty, "landuse"), "landuse") ? "bg-red-100" : ""}`}> */}
                          <tr className="border-b">
                            <td className="p-2 font-medium">Land Use</td>
                            <td className="p-2">{hasUsableValue(selectedProperty.landuse_admin) ? selectedProperty.landuse_admin : "N/A"}</td>
                            {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "landuse")}</td> */}
                            {selectedProperty.reassess == 1 && (
                              <td className="p-2">
                                {(() => {
                                  const reassessVal = hasUsableValue(selectedProperty.landuse_ml)
                                    ? selectedProperty.landuse_ml
                                    : selectedProperty.land_use_pred;
                                  return hasUsableValue(reassessVal) ? reassessVal : "N/A";
                                })()}
                              </td>
                            )}
                          </tr>
                          {/* <tr className={`border-b ${areValuesDifferent(selectedProperty.landarea_admin, getMLDisplayValue(selectedProperty, "landarea"), "landarea") ? "bg-red-100" : ""}`}> */}
                          <tr className="border-b">
                            <td className="p-2 font-medium">Land Area</td>
                            <td className="p-2">
                              {hasUsableValue(selectedProperty.landarea_admin)
                                ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                                : "N/A"}
                            </td>
                            {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "landarea")}</td> */}
                            {selectedProperty.reassess == 1 && (
                              <td className="p-2">
                                {(() => {
                                  const reassessVal = hasUsableValue(selectedProperty.landarea_ml)
                                    ? selectedProperty.landarea_ml
                                    : selectedProperty.landarea_combined_pred;
                                  return formatTwoDecimals(reassessVal) || (hasUsableValue(reassessVal) ? reassessVal : "N/A");
                                })()}
                              </td>
                            )}
                          </tr>
                          {/* <tr className={`border-b ${areValuesDifferent(selectedProperty.coveredarea_admin, getMLDisplayValue(selectedProperty, "coveredarea"), "coveredarea") ? "bg-red-100" : ""}`}> */}
                          <tr className="border-b">
                            <td className="p-2 font-medium">Covered Area</td>
                            <td className="p-2">
                              {hasUsableValue(selectedProperty.coveredarea_admin)
                                ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                                : "N/A"}
                            </td>
                            {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "coveredarea")}</td> */}
                            {selectedProperty.reassess == 1 && (
                              <td className="p-2">
                                {(() => {
                                  const reassessVal = hasUsableValue(selectedProperty.coveredarea_ml)
                                    ? selectedProperty.coveredarea_ml
                                    : selectedProperty.coveredarea_combined_pred;
                                  return formatTwoDecimals(reassessVal) || (hasUsableValue(reassessVal) ? reassessVal : "N/A");
                                })()}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {selectedProperty.meeting_requested == 1 && !selectedProperty.meeting_resolved && (
                  <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-md font-semibold text-purple-900 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> In-Person Meeting Requested
                      </h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Your supervisor has asked to meet in person about this property.
                        {selectedProperty.meeting_requested_at &&
                          ` Requested on ${new Date(selectedProperty.meeting_requested_at).toLocaleString()}.`}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post("/properties/resolve-meeting", {
                            propertyId: selectedProperty.id,
                            userId,
                          });
                          await refreshSelectedProperty(selectedProperty.id);
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to mark meeting as resolved.");
                        }
                      }}
                      className="flex-shrink-0 text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      Mark Resolved
                    </button>
                  </div>
                )}

                <div className="mt-6 bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h3 className="font-semibold text-gray-900">Comment Timeline</h3>
                    <div className="text-xs text-gray-500">Chat view</div>
                  </div>

                  {timelineLoading ? (
                    <div className="text-sm text-gray-600">Loading...</div>
                  ) : timelineError ? (
                    <div className="text-sm text-red-600">{timelineError}</div>
                  ) : (timeline || []).length === 0 ? (
                    <div className="text-sm text-gray-600">No previous comments found for this property.</div>
                  ) : (
                    <div className="space-y-3">
                      {timeline.map((item) => {
                        const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                        const role = String(item.user_role || "").toLowerCase();
                        const isSupervisor = item.type === "supervisor" || role === "supervisor";
                        const isInspector = item.type === "inspector_explanation" || role === "inspector";
                        const isAction = item.type === "action";

                        const actionText = (() => {
                          if (!isAction) return "";
                          if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                          if (item.action === "ACCEPT_PROPERTY") return "Resolved";
                          if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                          return String(item.action || "Action");
                        })();

                        const rows = item?.rows || {};
                        const rowEntries = [
                          ["Floor", rows.floor],
                          ["Land Use", rows.landuse],
                          ["Land Area", rows.landarea],
                          ["Covered Area", rows.coveredarea],
                        ].filter(([, v]) => hasUsableValue(v));

                        if (isAction) {
                          return (
                            <div key={item.id} className="flex justify-center">
                              <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                {actionText}
                                {createdAt ? ` • ${createdAt}` : ""}
                              </div>
                            </div>
                          );
                        }

                        const sideClass = isSupervisor ? "justify-start" : "justify-end";
                        const bubbleClass = item.isYellow
                          ? "bg-yellow-50 border-yellow-200 text-yellow-900"
                          : isSupervisor
                            ? "bg-gray-50 border-gray-200 text-gray-900"
                            : "bg-blue-50 border-blue-200 text-blue-900";
                        const headerLabel = item.isYellow
                          ? "Supervisor Flag Comment"
                          : isSupervisor
                            ? "Supervisor comment"
                            : isInspector
                              ? "Inspector comment"
                              : "Comment";

                        return (
                          <div key={item.id} className={`flex ${sideClass}`}>
                            <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                {/* <div className="text-xs font-semibold">
                                  {headerLabel}
                                  {item.user_name ? ` • ${item.user_name}` : ""}
                                  {item.stage ? ` • ${item.stage}` : ""}
                                </div> */}
                                <div className="text-xs font-semibold">
                                  Supervisor {item.user_name || "Supervisor"} Flagged this property
                                </div>
                                {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                              </div>

                              {item.type === "inspector_explanation" ? (
                                <div className="space-y-2">
                                  {hasUsableValue(item.overall) && (
                                    <div className="text-sm whitespace-pre-wrap">{item.overall}</div>
                                  )}
                                  {rowEntries.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {rowEntries.map(([k, v]) => (
                                        <div key={k} className="bg-white/70 border border-black/5 rounded-lg p-2">
                                          <div className="text-[11px] font-semibold opacity-80">{k}</div>
                                          <div className="text-sm whitespace-pre-wrap">{v}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm whitespace-pre-wrap">{item.text || ""}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* <div className="border border-gray-200 rounded-xl p-4">
                    <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center">
                      <MessageSquare className="h-4 w-4 mr-2 text-blue-600" />
                      Inspection Comments
                    </h3>
                    <textarea
                      rows={2}
                      placeholder="Your Comments ..."
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      disabled={submitting}
                      data-click-id="flagged_by_Supervisor_inspection_comments"
                      data-property-id={selectedProperty?.pin}
                    />
                    <div className="flex justify-between items-center mt-2">
                      <button
                        onClick={handleSaveCommentOnly}
                        disabled={!comment.trim() || submitting}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                        data-click-id="flagged_by_Supervisor_comments_save"
                        data-property-id={selectedProperty?.pin}
                      >
                        <Save className="h-3 w-3 inline mr-1" />
                        Save Comment
                      </button>
                    </div>
                  </div> */}

                  <div className="border border-gray-200 rounded-xl p-4 lg:col-span-2">
                    <h3 className="text-md font-semibold text-gray-900 mb-2 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />
                      {/* {hasInspectorFinalDecision(selectedProperty, userId) */}
                      {hasInspectorFinalDecision(selectedProperty)
                        ? "Edit Final Decision"
                        : "Final Decision"}
                    </h3>
                    {(() => {
                      // const hasInspectorDecision = hasInspectorFinalDecision(
                      //   selectedProperty,
                      //   userId
                      // );
                      const hasInspectorDecision = hasInspectorFinalDecision(selectedProperty);
                      const isSupervisorDecisionMade = isSupervisorDecisionLocked(selectedProperty);

                      if (isSupervisorDecisionMade) {
                        return (
                          <div className="text-center py-4">
                            <p className="text-gray-500">Supervisor has already made a decision.</p>
                            <p className="text-sm text-gray-400">Cannot edit final decision.</p>
                          </div>
                        );
                      }

                      if (hasInspectorDecision) {
                        return (
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => handleReassessAction("no-reassess")}
                              disabled={submitting}
                              className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                              data-click-id="flagged_by_Supervisor_edit_do_not_reassess"
                              data-property-id={selectedProperty?.pin}
                            >
                              <XCircle className="h-5 w-5 text-gray-600 mx-auto mb-1" />
                              <p className="text-sm font-semibold text-gray-800">Edit: Reject Flag</p>
                              <p className="text-xs text-gray-600">
                                {submitting && actionType === "no-reassess" ? "Processing..." : "Change decision (Do Not Reassess)"}
                              </p>
                            </button>
                            <button
                              onClick={() => handleReassessAction("reassess")}
                              disabled={submitting}
                              className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                              data-click-id="flagged_by_Supervisor_edit_reassess"
                              data-property-id={selectedProperty?.pin}
                            >
                              <CheckCircle className="h-5 w-5 text-gray-600 mx-auto mb-1" />
                              <p className="text-sm font-semibold text-gray-800">Edit: Accept Flag</p>
                              <p className="text-xs text-gray-600">
                                {submitting && actionType === "reassess" ? "Processing..." : "Change decision (Reassess)"}
                              </p>
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => handleReassessAction("no-reassess")}
                            disabled={submitting}
                            className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                          >
                            <XCircle className="h-5 w-5 text-gray-600 mx-auto mb-1" />
                            <p className="text-sm font-semibold text-gray-800">Reject Flag</p>
                            <p className="text-xs text-gray-600">
                              {submitting && actionType === "no-reassess" ? "Processing..." : "Do Not Reassess"}
                            </p>
                          </button>
                          <button
                            onClick={() => handleReassessAction("reassess")}
                            disabled={submitting}
                            className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                          >
                            <CheckCircle className="h-5 w-5 text-gray-600 mx-auto mb-1" />
                            <p className="text-sm font-semibold text-gray-800">Accept Flag</p>
                            <p className="text-xs text-gray-600">
                              {submitting && actionType === "reassess" ? "Processing..." : "Reassess & Update Features"}
                            </p>
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showNoReassessDetailsModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Property Comment and PT-5</h2>
              <label className="block font-medium text-gray-700 mb-2">
                Comment <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder="Enter comment..."
                className="w-full p-3 border rounded mb-4"
                disabled={submitting}
              />
              <label className="block font-medium text-gray-700 mb-2">
                PT-5 Serial Number <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Enter PT-5 Serial Number..."
                className="w-full p-3 border rounded mb-4"
                disabled={submitting}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!comment.trim()) {
                      toast.error("Comment is required.");
                      return;
                    }
                    setShowNoReassessDetailsModal(false);
                    setShowNotReassessModal(true);
                  }}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded"
                >
                  Next
                </button>
                <button
                  onClick={() => {
                    setShowNoReassessDetailsModal(false);
                    setActionType(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showNotReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Reason for Rejecting Flag (Do Not Reassess)</h2>
              <textarea
                value={notReassessComment}
                onChange={(e) => setNotReassessComment(e.target.value)}
                rows={5}
                placeholder="Enter reason / comments..."
                className="w-full p-3 border rounded mb-3"
              />
              {!notReassessComment.trim() && (
                <p className="text-red-500 text-sm mb-3">Reason is required</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleNoReassess}
                  disabled={!notReassessComment.trim() || submitting}
                  className={`flex-1 px-4 py-2 rounded text-white ${notReassessComment.trim()
                    ? "bg-gray-700 hover:bg-gray-800"
                    : "bg-gray-400 cursor-not-allowed"
                    }`}
                >
                  {submitting ? "Saving..." : "Confirm"}
                </button>
                <button
                  onClick={() => {
                    setShowNotReassessModal(false);
                    setShowNoReassessDetailsModal(true);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reference Modal */}
        <AnimatePresence>
          {showRefModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                {/* <h2 className="text-xl font-bold mb-4">
                  {selectedProperty?.refno || selectedProperty?.pt5 || selectedProperty?.pt_5 || selectedProperty?.pt
                    ? "Edit PT-5 Serial Number"
                    : "Enter PT-5 Serial Number"}
                </h2> */}

                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  {pt6IssueDate ? "Edit PT-6 Issue Date" : "Enter PT-6 Issue Date"}
                </h2>

                {/* <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="PT-5 Serial Number (Mandatory)"
                  className="w-full p-3 border rounded mb-2"
                  required
                /> */}

                {referenceNumber && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">PT-5 Serial:</span> {referenceNumber}
                    </p>
                  </div>
                )}

                <label className="block font-medium text-gray-700 mb-2">
                  PT-6 Issue Date <span className="text-gray-400">*</span>
                </label>
                <input
                  type="date"
                  value={pt6IssueDate}
                  onChange={(e) => setPt6IssueDate(e.target.value)}
                  className="w-full p-3 border rounded mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mb-4">
                  Date when PT-6 was issued
                </p>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      if (!pt6IssueDate) {
                        toast.error("PT-6 Issue Date is mandatory.");
                        return;
                      }
                      setShowRefModal(false);
                      setReadyToSubmit(true);
                    }}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {submitting ? "Saving..." : (selectedProperty?.refno || selectedProperty?.pt5 || selectedProperty?.pt_5 || selectedProperty?.pt ? "Update" : "Save")}
                  </button>
                  <button onClick={() => setShowRefModal(false)} className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded">
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Full Screen Image Modal */}
        {showFullImage && selectedProperty?.image && (
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fadeIn"
            onClick={() => {
              setShowFullImage(false);
              setRotation(0);
            }}
          >
            <div className="absolute top-6 right-6 flex space-x-4 z-10">
              <button
                onClick={handleRotate}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                title="Rotate Image"
              >
                <RotateCw className="h-6 w-6 text-white" />
              </button>
              {rotation !== 0 && (
                <button
                  onClick={handleSaveRotation}
                  disabled={savingImageRotation}
                  className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                  title="Save Rotation"
                >
                  {savingImageRotation ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CheckCircle className="h-6 w-6" />
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  setShowFullImage(false);
                  setRotation(0);
                }}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <motion.div
              initial={{ scale: 0.9, rotate: 0 }}
              animate={{ scale: 1, rotate: rotation }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                alt="Property Full View"
                className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        )}

        {/* Reassess Modal */}
        {showReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 via-white to-green-50">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-green-100 to-green-200 rounded-xl shadow-sm">
                    <Edit className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Update Property Features</h2>
                    <div className="flex items-center mt-1 space-x-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-sm font-semibold shadow-sm">
                        <MapPin className="h-3 w-3 mr-1" />
                        PIN: {selectedProperty.pin}
                      </span>
                      {/* {(hasPredictions(selectedProperty) || hasMLPredictions(selectedProperty)) && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 text-sm font-semibold shadow-sm">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Existing Assessments Found
                        </span>
                      )} */}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowReassessModal(false);
                    setFeatures([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors shadow-sm hover:shadow"
                  disabled={submitting}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Property Features</h3>
                  </div>

                  {/* {(hasPredictions(selectedProperty) || hasMLPredictions(selectedProperty)) && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-gray-200">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0 text-gray-600" />
                        <div>
                          <p className="font-medium mb-1 text-gray-800">This property has existing assessments</p>
                          <p className="text-sm text-gray-700">
                            Review the Admin and ML data below. Enter your inspection values in the "Inspector Edit" column.
                          </p>
                        </div>
                      </div>
                    </div>
                  )} */}

                  <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Feature</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Admin Data</th>
                          {/* <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">ML (Computer Vision Prediction)</th> */}
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Inspector Edit</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Explanation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {features.map((feature, index) => (
                          // <tr
                          //   key={feature.key}
                          //   className={`border-b border-gray-200 last:border-0 hover:bg-gray-50 ${
                          //     areValuesDifferent(feature.admin, feature.ml) ? "bg-red-100" : ""
                          //   }`}
                          // >
                          <tr
                            key={feature.key}
                            className={`border-b border-gray-200 last:border-0 hover:bg-gray-50 ${
                              // HIGHLIGHT DISABLED - uncomment below to re-enable mismatch highlighting (paired with ML column being hidden)
                              // areValuesDifferent(feature.admin, feature.ml) ? "bg-red-100" : ""
                              ""
                              }`}
                          >
                            <td className="py-4 px-6">
                              <div className="font-medium text-gray-900">{feature.label}</div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-800 border border-blue-200">
                                {feature.admin || "—"}
                              </div>
                            </td>
                            {/* <td className="py-4 px-6">
                              <div
                                className={`px-3 py-1.5 rounded-lg ${
                                  feature.ml && feature.ml !== feature.admin
                                    ? "bg-purple-50 text-purple-800 border border-purple-200"
                                    : "bg-gray-50 text-gray-700"
                                }`}
                              >
                                {feature.ml || "—"}
                              </div>
                            </td> */}
                            <td className="py-4 px-6">
                              {feature.key === "landuse" ? (
                                <select
                                  value={feature.inspector}
                                  onChange={(e) => {
                                    updateFeature(index, "inspector", e.target.value);
                                    const map = { Residential: "flagged_by_S_edit_reassess_inspector_edit_select_land_use", Mixed: "flagged_by_S_edit_reassess_inspector_edit_select_land_use", Commercial: "flagged_by_S_edit_reassess_inspector_edit_select_land_use" };
                                    if (e.target.value) import("../../utils/clickTracking").then(({ trackAction }) => trackAction("flagged_by_S_edit_reassess_inspector_edit_select_land_use", selectedProperty?.pin));
                                  }}
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm bg-white"
                                  disabled={submitting}
                                  data-click-id="flagged_by_S_edit_reassess_inspector_edit_select_land_use"
                                  data-property-id={selectedProperty?.pin}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : (feature.key === "landarea" || feature.key === "coveredarea") ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  type="text"
                                  value={feature.inspector}
                                  onChange={(e) => updateFeature(index, "inspector", e.target.value)}
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                  placeholder="Enter value"
                                  disabled={submitting}
                                  data-click-id={
                                    feature.key === "floor" ? "flagged_by_S_edit_reassess_inspector_edit_floor"
                                      : undefined
                                  }
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                            <td className="py-4 px-6">
                              {(feature.key === "landarea" || feature.key === "coveredarea") ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  type="text"
                                  value={rowExplanations[feature.key]}
                                  onChange={(e) => updateRowExplanation(feature.key, e.target.value)}
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                  placeholder={`Explain ${feature.label.toLowerCase()} change...`}
                                  disabled={submitting}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                    PT-5 Serial Number <span className="text-gray-400 font-normal text-sm ml-2">(optional)</span>
                  </h3>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="Enter PT-5 Serial Number..."
                    className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm"
                    disabled={submitting}
                    data-property-id={selectedProperty?.pin}
                  />
                </div>

                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                    Overall Explanation
                  </h3>
                  <textarea
                    rows={3}
                    placeholder="Provide an overall explanation for the reassessment..."
                    className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={submitting}
                    data-click-id="flagged_by_S_edit_reassess_add_comments"
                    data-property-id={selectedProperty?.pin}
                  />
                  <p className="text-sm text-gray-500 mt-2 flex items-center">
                    <Info className="h-3 w-3 mr-1" />
                    Comments will be automatically saved as "(Property reassessed)"
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowReassessModal(false);
                      setFeatures([]);
                    }}
                    disabled={submitting}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all shadow-sm hover:shadow disabled:opacity-50"
                    data-click-id="flagged_by_S_edit_reassess_cancel"
                    data-property-id={selectedProperty?.pin}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!comment.trim()) {
                        toast.error("Overall Explanation is mandatory.");
                        return;
                      }
                      setShowReassessModal(false);
                      setShowRefModal(true);
                    }}
                    disabled={submitting}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 rounded-lg font-medium flex items-center transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <DuplicatePt5Modal
          isOpen={showDuplicatePt5Modal}
          onClose={closeDuplicatePt5Modal}
          onSave={saveDuplicatePt5Retry}
          submitting={submitting}
        />
      </div>
    </div>
  );
};

export default Assigned;
